import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { mappingService } from '../../services/mappingService';
import { importService } from '../../services/importService';

/*
 * Generic import modal — works for any import type.
 * Pass a `config` object from importConfig.js (title, urlType, importType).
 * Replaces the old GLImportModal (which hardcoded GL service calls).
 */
const ImportModal = ({ isOpen, onClose, config }) => {
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState('');
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Import mode:
  //   'upsert'     -> add new + update existing, leave everything else untouched
  //   'deactivate' -> soft refresh: rows not in the file become is_active=false
  //   'replace'    -> hard delete all existing rows first (cascades on confirm)
  const [importMode, setImportMode] = useState('upsert');
  // Holds a structured error to render in-modal instead of a bare alert().
  // Shape: { message, detail, code, row, key } (any field may be absent).
  const [importError, setImportError] = useState(null);
  // Progress bar state (driven by chunked uploads).
  const [progress, setProgress] = useState(0);        // 0-100
  const [progressLabel, setProgressLabel] = useState('');

  // Load only the mapping profiles for THIS import type
  useEffect(() => {
    if (isOpen && config) {
      mappingService
        .getMappings(config.importType)
        .then((result) => {
          if (result.success) setProfiles(result.data);
        })
        .catch((err) => console.error('Failed to load profiles:', err));
    }
  }, [isOpen, config]);

  // Reset selection whenever the modal reopens / switches type
  useEffect(() => {
    if (isOpen) {
      setSelectedProfileId('');
      setFile(null);
      setImportMode('upsert');
      setImportError(null);
      setProgress(0);
      setProgressLabel('');
    }
  }, [isOpen, config]);

  if (!isOpen || !config) return null;

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleImport = async () => {
    if (!file || !selectedProfileId) {
      return alert('Please select a mapping version and a file.');
    }

    setImportError(null);
    setIsProcessing(true);
    setProgress(0);
    setProgressLabel('Preparing…');

    try {
      if (importMode === 'replace') {
        setProgressLabel('Deleting existing records…');
        try {
          // First attempt: no force. Backend returns 409 if dependent
          // budget/actuals records exist.
          await importService.deleteAllRecords(config.urlType, false);
        } catch (delErr) {
          const resp = delErr.response;
          if (resp && resp.status === 409 && resp.data?.requiresConfirmation) {
            // Build a human-readable breakdown of what will be destroyed.
            const deps = resp.data.dependents || [];
            const lines = deps
              .map((d) => `  • ${d.label}: ${d.count.toLocaleString()} record(s)`)
              .join('\n');
            const hasDestructive = deps.some((d) => d.destructive);

            const confirmed = window.confirm(
              `${resp.data.message}\n\n` +
                `The following will also be deleted:\n${lines}\n\n` +
                (hasDestructive
                  ? 'Budgets and actuals will need to be re-captured after this.\n\n'
                  : '') +
                'Are you absolutely sure you want to continue?'
            );

            if (!confirmed) {
              setIsProcessing(false);
              return; // user backed out — import is cancelled, nothing deleted
            }

            // Second attempt: force the cascade now that they've confirmed.
            await importService.deleteAllRecords(config.urlType, true);
          } else {
            throw delErr; // a real failure, not a confirmation prompt
          }
        }
      }
    } catch (error) {
      console.error('Failed to delete existing records:', error);
      const data = error.response?.data;
      setImportError({
        message: data?.message || 'Failed to delete existing records. Import cancelled.',
        detail: data?.detail || null,
        code: data?.code || null,
      });
      setIsProcessing(false);
      return;
    }

    const activeProfile = profiles.find(
      (p) => p.profile_id.toString() === selectedProfileId
    );
    const mappingConfig = activeProfile.mapping_config;

    const reader = new FileReader();

    // Send the file in batches so we can show real progress (and stay well
    // under the server's request-size limit). Each batch is its own backend
    // transaction; on the final batch we pass the full key list so a
    // deactivate-refresh considers the whole file, not just the last batch.
    const CHUNK_SIZE = 500;

    reader.onload = async (e) => {
      let processedRows = 0; // rows fully committed before the current batch
      let totalRows = 0;
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const rawData = XLSX.utils.sheet_to_json(worksheet, { defval: null });

        if (rawData.length === 0) {
          throw new Error('The uploaded file is empty.');
        }

        // Remap each row's headers -> DB column names using the saved profile
        const formattedData = rawData.map((row) => {
          const newRow = {};
          for (const [dbColumn, excelHeader] of Object.entries(mappingConfig)) {
            const cellValue = row[excelHeader];
            newRow[dbColumn] =
              typeof cellValue === 'string' ? cellValue.trim() : cellValue;
          }
          return newRow;
        });

        totalRows = formattedData.length;
        const deactivate = importMode === 'deactivate';
        // Full set of key values, used only by the final batch's deactivate step.
        const allKeys =
          deactivate && config.keyColumn
            ? formattedData
                .map((r) => r[config.keyColumn])
                .filter((v) => v !== null && v !== undefined && v !== '')
            : null;

        let inserted = 0;
        let deactivated = 0;
        setProgress(0);
        setProgressLabel(`Importing 0 / ${totalRows.toLocaleString()} rows…`);

        const batchCount = Math.ceil(totalRows / CHUNK_SIZE);
        for (let start = 0, b = 1; start < totalRows; start += CHUNK_SIZE, b++) {
          const chunk = formattedData.slice(start, start + CHUNK_SIZE);
          const isLast = start + CHUNK_SIZE >= totalRows;

          const t0 = performance.now();
          console.log(`[import] sending batch ${b}/${batchCount} (rows ${start + 1}-${Math.min(start + CHUNK_SIZE, totalRows)})…`);
          const result = await importService.importRecords(config.urlType, chunk, {
            deactivateMissing: deactivate && isLast,
            allKeys: deactivate && isLast ? allKeys : undefined,
          });
          console.log(`[import] batch ${b}/${batchCount} done in ${Math.round(performance.now() - t0)}ms`, result);

          inserted += result.insertedCount || 0;
          deactivated += result.deactivatedCount || 0;
          processedRows = Math.min(start + CHUNK_SIZE, totalRows);
          setProgress(Math.round((processedRows / totalRows) * 100));
          setProgressLabel(
            `Importing ${processedRows.toLocaleString()} / ${totalRows.toLocaleString()} rows…`
          );
        }

        const extra =
          deactivated > 0
            ? ` ${deactivated} record(s) not in the file were deactivated.`
            : '';
        alert(`Successfully imported ${inserted} ${config.title}!${extra}`);
        onClose();
      } catch (error) {
        const status = error.response?.status;
        const data = error.response?.data;
        // Backend reports the row number relative to its batch; convert it to a
        // row number in the whole file so the user can find it.
        const globalRow =
          data && data.row ? processedRows + data.row : null;
        // Log everything useful to the console for debugging.
        console.error('Import processing failed:', {
          status,
          data,
          processedRows,
          totalRows,
          message: error.message,
        });

        if (status === 413) {
          // Body exceeded the server's JSON limit — batch size too large.
          setImportError({
            message: `A batch was too large for the server. Lower CHUNK_SIZE in ImportModal, or ask an admin to raise the server import size limit.`,
            code: '413',
          });
        } else if (data && typeof data === 'object') {
          // Structured error from the backend (enum/FK/not-null/etc.)
          setImportError({
            message: data.message || `Import failed${status ? ` (HTTP ${status})` : ''}.`,
            detail: data.detail || null,
            code: data.code || (status ? String(status) : null),
            row: globalRow,
            key: data.key || null,
            note:
              data.note ||
              (processedRows > 0
                ? `${processedRows.toLocaleString()} earlier row(s) were already saved before this error.`
                : null),
          });
        } else if (typeof data === 'string' && data.trim()) {
          // Non-JSON error body (e.g. an Express/proxy error page).
          setImportError({
            message: `Import failed${status ? ` (HTTP ${status})` : ''}.`,
            detail: data.slice(0, 500),
            code: status ? String(status) : null,
          });
        } else {
          // Client-side failure (file parse, network, etc.)
          setImportError({
            message: error.message || 'An error occurred while processing the file.',
          });
        }
      } finally {
        setIsProcessing(false);
      }
    };

    reader.onerror = () => {
      console.error('File reading error');
      setImportError({ message: 'Failed to read the file. Please try again.' });
      setIsProcessing(false);
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-xl">
        <h2 className="mb-4 text-xl font-bold text-slate-900">Import {config.title}</h2>

        {/* Profile Selector */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-slate-700">Select Mapping Version</label>
          <select
            className="w-full p-2 border rounded border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-500"
            value={selectedProfileId}
            onChange={(e) => setSelectedProfileId(e.target.value)}
          >
            <option value="" disabled>-- Select a Version --</option>
            {profiles.map((p) => (
              <option key={p.profile_id} value={p.profile_id}>
                {p.profile_name}
              </option>
            ))}
          </select>
          {profiles.length === 0 && (
            <p className="mt-1 text-xs text-amber-600">
              No mapping versions found for {config.title}. Create one with "Map" first.
            </p>
          )}
        </div>

        {/* File Input */}
        <div className="mb-4">
          <label className="block mb-2 text-sm font-medium text-slate-700">Upload Excel/CSV File</label>
          <input
            type="file"
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            onChange={handleFileChange}
            className="w-full text-sm text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-slate-50 file:text-slate-700 hover:file:bg-slate-100"
          />
        </div>

        {/* Import mode */}
        <div className="mb-6">
          <label className="block mb-2 text-sm font-medium text-slate-700">Import mode</label>
          <div className="space-y-2">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="importMode"
                value="upsert"
                checked={importMode === 'upsert'}
                onChange={(e) => setImportMode(e.target.value)}
                className="mt-1 w-4 h-4 border-slate-300 text-slate-900 cursor-pointer"
              />
              <span className="text-sm text-slate-700 select-none">
                <span className="font-medium">Add &amp; update</span>
                <span className="block text-xs text-slate-400">
                  Adds new records and updates matches. Nothing else is touched.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="importMode"
                value="deactivate"
                checked={importMode === 'deactivate'}
                onChange={(e) => setImportMode(e.target.value)}
                className="mt-1 w-4 h-4 border-slate-300 text-slate-900 cursor-pointer"
              />
              <span className="text-sm text-slate-700 select-none">
                <span className="font-medium">Refresh (recommended)</span>
                <span className="block text-xs text-slate-400">
                  Updates the file's records and deactivates any not in the file.
                  Budgets and history are preserved.
                </span>
              </span>
            </label>

            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="importMode"
                value="replace"
                checked={importMode === 'replace'}
                onChange={(e) => setImportMode(e.target.value)}
                className="mt-1 w-4 h-4 border-slate-300 text-red-600 cursor-pointer"
              />
              <span className="text-sm text-slate-700 select-none">
                <span className="font-medium text-red-700">Replace all (destructive)</span>
                <span className="block text-xs text-slate-400">
                  Deletes every existing record first. Linked budgets and actuals
                  will also be deleted and must be re-captured.
                </span>
              </span>
            </label>
          </div>
        </div>

        {/* Progress bar — shown while a (chunked) import is running */}
        {isProcessing && (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1 text-xs text-slate-500">
              <span>{progressLabel || 'Processing…'}</span>
              <span className="font-mono">{progress}%</span>
            </div>
            <div className="w-full h-2 overflow-hidden rounded-full bg-slate-200">
              <div
                className="h-full bg-slate-900 transition-all duration-200 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Error panel — shows specific DB/import errors in-modal */}
        {importError && (
          <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 flex-shrink-0 text-red-500 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-red-800">Import failed</p>
                <p className="text-sm text-red-700 break-words">{importError.message}</p>
                {importError.note && (
                  <p className="mt-1 text-xs text-red-600">{importError.note}</p>
                )}
                {(importError.code || importError.row) && (
                  <p className="mt-1 text-xs text-red-500 font-mono">
                    {importError.row ? `row ${importError.row}` : ''}
                    {importError.row && importError.code ? ' · ' : ''}
                    {importError.code ? `code ${importError.code}` : ''}
                  </p>
                )}
                {importError.detail && importError.detail !== importError.message && (
                  <details className="mt-1">
                    <summary className="text-xs text-red-500 cursor-pointer select-none">Technical detail</summary>
                    <p className="mt-1 text-xs text-red-500 font-mono break-words">{importError.detail}</p>
                  </details>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="px-4 py-2 text-sm text-slate-600 border rounded border-slate-300 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={handleImport}
            disabled={isProcessing || !file || !selectedProfileId}
            className="px-4 py-2 text-sm text-white rounded bg-slate-900 hover:bg-slate-800 disabled:opacity-50"
          >
            {isProcessing ? 'Processing...' : 'Import Data'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImportModal;